import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allArticles, categoryNames } from '@/mocks/article';
import { categories } from '@/mocks/home';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';
  const [animatedTab, setAnimatedTab] = useState(activeTab);

  useEffect(() => {
    setAnimatedTab(activeTab);
  }, [activeTab]);

  const filteredArticles = activeTab === 'all'
    ? allArticles
    : allArticles.filter((a) => a.categoryId === activeTab);

  const handleTabChange = (tabId: string) => {
    setSearchParams(tabId === 'all' ? {} : { tab: tabId });
  };

  const currentCategoryName = activeTab === 'all' ? '全部内容' : categoryNames[activeTab] || '全部内容';

  return (
    <main className="pt-20 md:pt-24 pb-14 md:pb-20 px-4 md:px-6 bg-background-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Breadcrumb items={[{ label: '分类浏览' }]} />
        </div>

        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">分类浏览</h1>
        <p className="text-sm text-foreground-500 mb-8">探索不同分类下的精品内容，发现你感兴趣的领域</p>

        <div className="flex flex-wrap items-center gap-2 mb-8 md:mb-10">
          <button
            onClick={() => handleTabChange('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
              activeTab === 'all'
                ? 'bg-foreground-900 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleTabChange(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === cat.id
                  ? 'bg-foreground-900 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {filteredArticles.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group block cursor-pointer"
            >
              <article className="bg-background-100 rounded-lg overflow-hidden transition-transform duration-300 group-hover:-translate-y-1 h-full flex flex-col">
                <div className="relative w-full h-48 md:h-52 overflow-hidden">
                  <img
                    src={article.cover}
                    alt={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    title={`${article.title} - 蜜桃传媒精品内容`}
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-background-50/90 text-xs font-medium text-foreground-700 whitespace-nowrap">
                    {article.category}
                  </span>
                </div>
                <div className="p-4 md:p-5 flex-1 flex flex-col">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed flex-1">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-user-line text-xs" />
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line text-xs" />
                        {article.views}
                      </span>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block" />
            <p className="text-foreground-500">该分类下暂无内容</p>
          </div>
        )}
      </div>
    </main>
  );
}